"use client";

import { useState, useContext } from "react";
import { useRouter } from "next/navigation";

import { ModalData } from "@/app/(workspace)/_hooks/useModal";
import { Button } from "@/components/ui/Button";
import { requestLodBackfill } from "@/lib/firebase/services/lodBackfill";
import { ToastContext } from "@/providers/ToastProvider";
import { useProjectModal } from "@/app/(workspace)/_providers/ModalProvider";
import { useSubscription } from "@/lib/firebase/hooks/useSubscription";
import { SUBSCRIPTION_LIMITS, SubscriptionTier } from "@/lib/firebase/types/subscription";
import type { LodProfile } from "@/app/(workspace)/_types/project";

const LOD_PROFILE_OPTIONS: Array<{
  id: LodProfile;
  name: string;
  requiredTier: SubscriptionTier | null;
}> = [
  { id: "fast", name: "Fast", requiredTier: null },
  { id: "balanced", name: "Balanced", requiredTier: SubscriptionTier.PRO },
  { id: "max", name: "Max", requiredTier: SubscriptionTier.ULTRA },
];

const TIER_ORDER: Record<string, number> = {
  [SubscriptionTier.STANDARD]: 0,
  [SubscriptionTier.PRO]: 1,
  [SubscriptionTier.ULTRA]: 2,
  [SubscriptionTier.ENTERPRISE]: 3,
};

const TIER_DISPLAY_NAMES: Record<string, string> = {
  [SubscriptionTier.STANDARD]: "Standard",
  [SubscriptionTier.PRO]: "Pro",
  [SubscriptionTier.ULTRA]: "Ultra",
  [SubscriptionTier.ENTERPRISE]: "Enterprise",
};

function getPlanTagStyle(tier: string): string {
  switch (tier) {
    case SubscriptionTier.STANDARD:
      return "bg-blue-100 text-blue-700 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-1 dark:ring-inset dark:ring-blue-400/20";
    case SubscriptionTier.PRO:
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-400 dark:ring-1 dark:ring-inset dark:ring-emerald-400/20";
    case SubscriptionTier.ULTRA:
      return "bg-rose-100 text-rose-700 dark:bg-rose-400/10 dark:text-rose-400 dark:ring-1 dark:ring-inset dark:ring-rose-400/20";
    case SubscriptionTier.ENTERPRISE:
      return "bg-amber-100 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-1 dark:ring-inset dark:ring-amber-400/20";
    default:
      return "bg-zinc-100 text-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400";
  }
}

function PlanTag({ tier }: { tier: string }) {
  const tierName = TIER_DISPLAY_NAMES[tier] || tier.toUpperCase();
  return (
    <span className={`ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${getPlanTagStyle(tier)}`}>
      {tierName}+
    </span>
  );
}

export function LodBackfillModal({
  data,
  onClose,
}: {
  data: ModalData;
  onClose: () => void;
}) {
  const toastContext = useContext(ToastContext);
  const router = useRouter();
  const { open } = useProjectModal();
  const { subscription } = useSubscription();
  const { projectId, initialLodProfile, initialUseExtSplats } = data;

  const [lodProfile, setLodProfile] = useState<LodProfile>(
    initialLodProfile ?? "fast"
  );
  const [useExtSplats, setUseExtSplats] = useState<boolean>(
    initialUseExtSplats ?? false
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const currentTier = subscription?.tier || SubscriptionTier.STANDARD;

  const isOptionAvailable = (requiredTier: string | null): boolean => {
    if (!requiredTier) return true;
    return TIER_ORDER[currentTier] >= TIER_ORDER[requiredTier];
  };

  const isHighPrecisionAvailable = (): boolean => {
    if (!subscription) return false;
    return SUBSCRIPTION_LIMITS[subscription.tier].highPrecision;
  };

  // 잠긴 LoD Profile 선택 시 업그레이드 모달로 교체
  const showUpgradeModal = (requiredTier: string) => {
    const tierName = TIER_DISPLAY_NAMES[requiredTier] || requiredTier.toUpperCase();
    open(
      {
        type: "message",
        title: "Upgrade Required",
        size: "sm",
        message: `This LoD profile requires ${tierName} plan or higher. Would you like to upgrade your plan?`,
        confirmText: "Upgrade",
        cancelText: "Cancel",
        onConfirm: () => {
          onClose();
          router.push("/subscription/upgrade");
        },
      },
      true // replace current modal
    );
  };

  const handleProfileSelect = (option: typeof LOD_PROFILE_OPTIONS[number]) => {
    if (option.requiredTier && !isOptionAvailable(option.requiredTier)) {
      showUpgradeModal(option.requiredTier);
      return;
    }
    setLodProfile(option.id);
  };

  // High Precision 토글 — Ultra+ 차등
  const handleHighPrecisionToggle = () => {
    if (!isHighPrecisionAvailable() && !useExtSplats) {
      showUpgradeModal(SubscriptionTier.ULTRA);
      return;
    }
    setUseExtSplats((v) => !v);
  };

  const handleSubmit = async () => {
    if (!projectId) {
      setError("Project ID is missing");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      await requestLodBackfill(projectId, { lodProfile, useExtSplats });

      // 부모(ProjectCard)에게 요청 성공을 알림 → "Queued..." 즉시 표시
      if (data.onConfirm) {
        await data.onConfirm();
      }

      toastContext?.showToast({
        type: "success",
        message: "LoD generation requested. It will take about 1-5 minutes.",
      });

      onClose();
    } catch (err) {
      console.error("Failed to request LoD backfill:", err);
      setError(err instanceof Error ? err.message : "Failed to request LoD generation");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="space-y-5">
        {/* 설명 */}
        <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/50 p-4 ring-1 ring-zinc-900/5 dark:ring-white/10">
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            Generate a LoD file to enable high-quality streaming for this project.
          </p>
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            Once complete, the viewer will smoothly play back large scenes. Takes about 1-5 minutes.
          </p>
          {initialLodProfile && (
            <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">
              Currently using <strong className="font-semibold">{initialLodProfile.charAt(0).toUpperCase() + initialLodProfile.slice(1)}</strong> profile. Re-generate with the same or a different one.
            </p>
          )}
        </div>

        {/* LoD Profile (플랜 차등) */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-400 mb-2">
            LoD Profile
          </label>
          <div className="grid grid-cols-3 gap-2">
            {LOD_PROFILE_OPTIONS.map((option) => {
              const isLocked = option.requiredTier && !isOptionAvailable(option.requiredTier);
              const isSelected = lodProfile === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleProfileSelect(option)}
                  className={`
                    group relative flex flex-col items-center justify-center rounded-lg border p-2.5 transition-colors
                    ${isSelected
                      ? "border-emerald-600 bg-emerald-600 dark:border-emerald-500 dark:bg-emerald-500"
                      : isLocked
                        ? "border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50 cursor-pointer hover:border-violet-400 dark:hover:border-violet-500"
                        : "border-zinc-300 bg-white dark:border-zinc-700 dark:bg-zinc-800 cursor-pointer hover:border-emerald-500 dark:hover:border-emerald-400"
                    }
                  `}
                >
                  <div className="flex items-center">
                    <span className={`
                      text-sm font-semibold
                      ${isSelected
                        ? "text-white"
                        : isLocked
                          ? "text-zinc-400 dark:text-zinc-500"
                          : "text-zinc-900 dark:text-white"
                      }
                    `}>
                      {option.name}
                    </span>
                    {option.requiredTier && <PlanTag tier={option.requiredTier} />}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400 leading-tight">
            Higher profiles produce better LoD quality but take longer to build.
          </p>
        </div>

        {/* High Precision (ExtSplats float32 좌표) — Ultra+ 전용 */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-400 mb-2">
            High Precision
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={useExtSplats}
              aria-label="Toggle high precision"
              onClick={handleHighPrecisionToggle}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 cursor-pointer
                ${useExtSplats
                  ? "bg-emerald-600 dark:bg-emerald-500"
                  : "bg-zinc-200 dark:bg-zinc-700"
                }
              `}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${useExtSplats ? "translate-x-5" : "translate-x-0"
                  }`}
              />
            </button>
            <PlanTag tier={SubscriptionTier.ULTRA} />
          </div>
          <p className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400 leading-tight">
            Reduces banding artifacts in large scenes by using float32 coordinate precision. Slightly higher memory usage.
          </p>
        </div>

        {error && (
          <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="filled" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Requesting..." : "Generate LoD"}
        </Button>
      </div>
    </div>
  );
}
